import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../common'
import * as models from '../models'

import * as expression from './expression'
import type * as record from './record'

// TODO: break this file up into smaller files (especially Value.Type)

/**
 * TODO:
 */
export type Value<TType extends Value.Type = Value.Type> =
  TType extends common.value.types.Intrinsic
    ? Value.Intrinsic<TType>
    : TType extends Value.Type.Reference
      ? Value.Reference.Default
      : TType extends Value.Type.Reference.Multi
        ? Value.Reference.Multi.Default
        : TType extends Value.Type.Enum | Value.Type.Enum.Inline
          ? unknown // TODO: We should probably try to be more specific
          : TType extends Value.Type.Structure | Value.Type.Structure.Inline
            ? object | readonly unknown[] // TODO: We should probably try to be more specific
            : TType extends Value.Type.Files
              ? readonly string[] // File names; actual bytes live in S3
              : never

export namespace Value {
  /**
   * Indicates what type of values a given attribute accepts
   */
  export type Type =
    | common.value.types.Intrinsic
    | Type.Enum
    | Type.Enum.Inline
    | Type.Structure
    | Type.Structure.Inline
    | Type.Reference
    | Type.Reference.Multi
    | Type.Files
  export namespace Type {
    export const isType: TypeChecker<Type> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.union(
        common.value.types.Intrinsic.isIntrinsic,
        Type.Enum.isEnum,
        Type.Structure.isStructure,
        Type.Reference.isReference,
        Type.Files.isFiles,
      ),
    ).usingAlias('dms.Value.Type')
    export const assertIsType: TypeAsserter<Type> =
      TypeAsserter.Factory.from(isType)

    /**
     * Concise readable text representation of the given {@link Type}
     *
     * __NOTE:__ this is primarily designed to be used with LLMs, so the result
     * should be formatted accordingly
     */
    export function represent(type: Type): string {
      if (common.value.types.Intrinsic.isIntrinsic(type)) {
        return type
      }
      if (Enum.isEnum(type)) {
        return `${type.isMultiselect ? 'Multi' : 'Single'} Select Enum (${type.enumId})`
      }
      if (Enum.Inline.isInline(type)) {
        // TODO: maybe add entries
        return `${type.inlineEnum.isMultiselect ? 'Multi' : 'Single'} Select Inline Enum`
      }
      if (Structure.isStructure(type)) {
        // TODO: do better
        return 'Structure'
      }
      if (Structure.Inline.isInline(type)) {
        // TODO: do better
        return 'Inline Structure'
      }
      if (Reference.isReference(type)) {
        return `References ${type.dimensionId}`
      }
      if (Reference.Multi.isMulti(type)) {
        return `References ${type.dimensionId} (Multi Select)`
      }
      if (Files.isFiles(type)) {
        return 'File List'
      }
      throw TypeAsserter.assertIsNever(type)
    }

    /**
     * {@link Value.Type} that corresponds to {@link common.value.types.Enum}
     */
    export interface Enum
      extends Pick<common.value.types.Enum, 'isMultiselect'> {
      enumId: models.Enum.Id
    }
    export namespace Enum {
      export const isEnum: TypeChecker<Enum> = TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.object<Enum>({
          enumId: models.Enum.Id.isId,
          isMultiselect: TypeChecker.isBoolean,
        }),
      ).usingAlias('Attribute.Type.Enum')
      export const assertIsEnum: TypeAsserter<Type> =
        TypeAsserter.Factory.from(isEnum)

      /**
       * Type definition for inline enums that are not associated with
       * any predefined {@link models.Structure}
       */
      export interface Inline {
        inlineEnum: common.value.types.Enum
      }
      export namespace Inline {
        export const isInline: TypeChecker<Inline> = TypeChecker.Factory.lazy(
          () =>
            TypeChecker.Factory.object<Inline>({
              inlineEnum: common.value.types.Enum.isEnum,
            }),
        ).usingAlias('Attribute.Type.InlineEnum')
        export const assertIsInline: TypeAsserter<Type> =
          TypeAsserter.Factory.from(isInline)

        export interface AsJson {
          inlineEnum: common.value.types.Enum.constructor.Params
        }
        export namespace AsJson {
          /**
           * Used when we need to check if a json object has the same structure
           * as our Enum class, i.e. has the same properties. We can't do that
           * with ordinary typechecker because it checks if the object we are
           * checking is instance of Enum class, which it really isn't
           */
          export const isAsJson = TypeChecker.Factory.lazy(() =>
            TypeChecker.Factory.object<AsJson>({
              inlineEnum: common.value.types.Enum.constructor.Params.isParams,
            }),
          ).usingAlias('Attribute.Type.InlineEnum.AsJson')
        }
      }
    }

    /**
     * {@link Value.Type} that corresponds to {@link common.value.types.Structure}
     */
    export interface Structure {
      structureId: models.Structure.Id
    }
    export namespace Structure {
      export const isStructure: TypeChecker<Structure> =
        TypeChecker.Factory.lazy(() =>
          TypeChecker.Factory.object<Structure>({
            structureId: models.Structure.Id.isId,
          }),
        ).usingAlias('Attribute.Type.Structure')
      export const assertIsStructure: TypeAsserter<Type> =
        TypeAsserter.Factory.from(isStructure)

      /**
       * Type definition for inline structures that are not associated with
       * any predefined {@link models.Structure}
       */
      export interface Inline {
        inlineStructure: common.value.types.Structure
      }
      export namespace Inline {
        export const isInline: TypeChecker<Inline> = TypeChecker.Factory.lazy(
          () =>
            TypeChecker.Factory.object<Inline>({
              inlineStructure: common.value.types.Structure.isStructure,
            }),
        ).usingAlias('Attribute.Type.InlineStructure')
        export const assertIsInline: TypeAsserter<Type> =
          TypeAsserter.Factory.from(isInline)
      }
    }

    /**
     * {@link Value.Type} for references to other {@link models.Dimension}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export interface Reference {
      dimensionId: models.Dimension.Id
      /**
       * __NOTE:__ this is always `false` for the {@link Reference} type and always
       * `true` for the {@link Reference.Multi} type. This distinction helps us
       * differentiate between the two types at runtime
       */
      isMultiselect?: false
    }
    export namespace Reference {
      export const isReference: TypeChecker<Reference> =
        TypeChecker.Factory.lazy(() =>
          TypeChecker.Factory.object<Reference>({
            dimensionId: models.Dimension.Id.isId,
            isMultiselect: TypeChecker.Factory.optional(TypeChecker.isFalse),
          }),
        ).usingAlias('Attribute.Type.Reference')
      export const assertIsReference: TypeAsserter<Type> =
        TypeAsserter.Factory.from(isReference)

      export interface Multi {
        dimensionId: models.Dimension.Id
        /**
         * Enables selection of multiple records from the referenced dimension
         *
         * When this flag is `true`, more than one record can be selected from
         * the dimension specified by dimensionId
         *
         * __NOTE:__ this is always true for the {@link Multi} type and always
         * false for the {@link Reference} type. This distinction helps us
         * differentiate between the two types at runtime
         */
        isMultiselect: true
      }
      export namespace Multi {
        export const isMulti: TypeChecker<Multi> = TypeChecker.Factory.lazy(
          () =>
            TypeChecker.Factory.object<Multi>({
              dimensionId: models.Dimension.Id.isId,
              isMultiselect: TypeChecker.isTrue,
            }),
        ).usingAlias('Attribute.Type.Reference.Multi')
        export const assertIsMulti: TypeAsserter<Type> =
          TypeAsserter.Factory.from(isMulti)
      }
    }

    /**
     * {@link Value.Type} for a list of uploaded files attached to a record.
     * The stored {@link Value} is a list of file names; the actual file
     * contents live in S3, keyed off of the record's identity (tenant, zone,
     * model, record) plus the file name, so no path is stored alongside the
     * value itself
     */
    export interface Files {
      /**
       * __NOTE:__ always `true`, this is purely a discriminant marking this
       * {@link Type} as a {@link Files} list (as opposed to some other type)
       */
      isFile: true
    }
    export namespace Files {
      export const isFiles: TypeChecker<Files> = TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.object<Files>({
          isFile: TypeChecker.isTrue,
        }),
      ).usingAlias('Attribute.Type.Files')
      export const assertIsFiles: TypeAsserter<Type> =
        TypeAsserter.Factory.from(isFiles)
    }

    /**
     * Produce a {@link common.value.types.Type} from a given
     * {@link Value.Type}
     *
     * __NOTE:__ for better accuracy for the type of the value, try passing
     * {@link Schema}
     *
     * If we can't determine the type we default to this {@link common.value.types.Intrinsic.Text}
     */
    export function toCommonValueType(
      type: Type,
      schema?: models.Schema,
    ): common.value.types.Type {
      // TODO: implement transformation for other types
      if (common.value.types.Intrinsic.isIntrinsic(type)) {
        return type
      } else if (Type.Enum.isEnum(type)) {
        const { enumId: identifiable, isMultiselect } = type
        return schema
          ? schema.getCommonEnum({ identifiable, isMultiselect })
          : common.value.types.Intrinsic.Text
      } else if (Type.Structure.isStructure(type)) {
        console.warn(
          `Common type discovery not implemented for 'Structure' returning default (OBJECT)`,
        )
        return common.value.types.Intrinsic.Object
      } else if (Type.Structure.Inline.isInline(type)) {
        return type.inlineStructure
      } else if (Type.Enum.Inline.isInline(type)) {
        return type.inlineEnum
      } else if (Type.Reference.isReference(type)) {
        return Value.Reference.Default.TYPE
      } else if (Type.Files.isFiles(type)) {
        // Stored as a list of file names
        return common.value.types.Intrinsic.List
      } else {
        console.warn(
          `Common type discovery not implemented for ${JSON.stringify(type, null, 2)} returning default (TEXT)`,
        )
        return common.value.types.Intrinsic.Text
      }
    }
  }

  /**
   * Type containing value and type information
   */
  export interface ValueWithType<TType extends Type = Type> {
    value: Value<TType> | undefined
    type: TType
  }

  export namespace ValueWithType {
    /** Strict type in order for discriminated union to be possible */
    export type Strict<TType extends Type = Type> = TType extends any
      ? ValueWithType<TType>
      : never

    export namespace Strict {
      /**
       * __NOTE:__ This is a workaround.
       *
       * {@link ValueWithType.Strict} is a discriminated union. However, trying
       * to discriminate with a {@link TypeChecker} (or any type predicate) does
       * not work (as of 07/03/2025). So, we created this function which allows
       * us to narrow the type of the discriminated union.
       */
      export const narrow = <TType extends Type>(
        val: ValueWithType.Strict,
        typeChecker: TypeChecker<TType>,
      ): val is ValueWithType.Strict<TType> => typeChecker(val.type)
    }
  }

  /**
   * TODO:
   */
  export type Intrinsic<
    TType extends common.value.types.Intrinsic = common.value.types.Intrinsic,
  > = common.value.Raw<TType>

  /**
   * TODO:
   */
  export type Reference<
    TFormat extends Reference.Format = Reference.Format,
    TEntityId extends models.Entity.Id | PartialId =
      | models.Entity.Id
      | PartialId,
  > = {
    [Reference.Format.Dimension]: Record<TEntityId, record.Record.Id>
    [Reference.Format.Entity]: {
      entityId: TEntityId
      recordId: record.Record.Id
    }
    [Reference.Format.EntityId]: TEntityId
    [Reference.Format.RecordId]: record.Record.Id
  }[TFormat]
  export namespace Reference {
    export namespace InFormat {
      /**
       * Alias for
       * {@link Value.Reference}<{@link Value.Reference.Format.Dimension}>
       */
      export type Dimension = Reference<Format.Dimension>
      export namespace Dimension {
        export const TYPE = common.value.types.Structure.object<Dimension>({
          known: {},
          // Record IDs
          others: common.value.types.Intrinsic.Text,
        })

        export const isDimension: TypeChecker<Dimension> =
          TypeChecker.Factory.lazy(() =>
            TypeChecker.Factory.custom(
              (value: unknown): value is Dimension =>
                TypeChecker.isObject(value) &&
                !TypeChecker.isArray(value) &&
                !Reference.Default.isDefault(value) &&
                Object.keys(value).length > 0 &&
                Object.keys(value).every(k => PartialId.isPartialId(k)) &&
                Object.values(value).every(
                  v => TypeChecker.isNull(v) || TypeChecker.isString(v),
                ),
              { typeDescription: 'Value.Reference.InFormat.Dimension' },
            ),
          ).usingAlias('Value.Reference.InFormat.Dimension')

        export const assertIsDimension: TypeAsserter<Dimension> =
          TypeAsserter.Factory.from(isDimension)
      }

      /**
       * Alias for
       * {@link Value.Reference}<{@link Value.Reference.Format.Entity}>
       */
      export type Entity = Reference<Format.Entity>
      export namespace Entity {
        export const TYPE = common.value.types.Structure.object<Entity>({
          known: {
            entityId: {
              isOptional: false,
              type: common.value.types.Intrinsic.Text,
            },
            recordId: {
              isOptional: false,
              type: common.value.types.Intrinsic.Text,
            },
          },
        })
      }

      /**
       * Alias for
       * {@link Value.Reference}<{@link Value.Reference.Format.EntityId}>
       */
      export type EntityId = Reference<Format.EntityId>
      export namespace EntityId {
        export const TYPE = common.value.types.Intrinsic.Text
      }

      /**
       * Alias for
       * {@link Value.Reference}<{@link Value.Reference.Format.RecordId}>
       */
      export type RecordId = Reference<Format.RecordId>
      export namespace RecordId {
        export const TYPE = common.value.types.Intrinsic.Text
      }
    }

    /**
     * Specifies what the return value should be for a reference attribute
     */
    export enum Format {
      /**
       * Returns an object where the keys are the partial or full IDs for the
       * entities in this reference attribute's dimension and the values are the
       * referenced record IDs for each of those entities
       *
       * ##### Example
       * ```ts
       * // --- `entityIdFormat: models.Id.Format.Partial` ---
       *
       * // References a building
       * {
       *   '!camp': '?123',
       *   '!bldg': '?456'
       * }
       * // References a room
       * {
       *   '!camp': '?123',
       *   '!bldg': '?456',
       *   '!room': '?789'
       * }
       *
       * // --- `entityIdFormat: models.Id.Format.Full` ---
       *
       * // References a building
       * {
       *   'ent:!space:!camp': '?123',
       *   'ent:!space:!bldg': '?456'
       * }
       * // References a room
       * {
       *   'ent:!space:!camp': '?123',
       *   'ent:!space:!bldg': '?456',
       *   'ent:!space:!room': '?789'
       * }
       * ```
       */

      Dimension = 'dim',
      /**
       * Returns an object with an `entityId` property that contains the partial
       * or full ID of the entity that is being referenced and a `recordId`
       * property that contains the ID of the record of that entity that is being
       * referenced
       *
       * ##### Example
       * ```ts
       * // --- `entityIdFormat: models.Id.Format.Partial` ---
       *
       * // References a building
       * {
       *   entityId: '!bldg',
       *   recordId: '?456'
       * }
       * // References a room
       * {
       *   entityId: '!room',
       *   recordId: '?789'
       * }
       *
       * // --- `entityIdFormat: models.Id.Format.Full` ---
       *
       * // References a building
       * {
       *   entityId: 'ent:!space:!bldg',
       *   recordId: '?456'
       * }
       * // References a room
       * {
       *   entityId: 'ent:!space:!room',
       *   recordId: '?789'
       * }
       * ```
       */

      Entity = 'ent',
      /**
       * Returns the partial or full ID of the entity that is being referenced
       *
       * ##### Example
       * ```ts
       * // --- `entityIdFormat: models.Id.Format.Partial` ---
       *
       * // References a building
       * '!bldg'
       * // References a room
       * '!room'
       *
       * // --- `entityIdFormat: models.Id.Format.Full` ---
       *
       * // References a building
       * 'ent:!space:!bldg'
       * // References a room
       * 'ent:!space:!room'
       * ```
       */
      EntityId = 'ent-id',
      /**
       * Returns the ID of the record that is being referenced
       *
       * ##### Example
       * ```ts
       * // References a building
       * '?456'
       * // References a room
       * '?789'
       * ```
       */
      RecordId = 'record-id',
    }
    // Needs to be before namespace is defined
    const FORMATS: ReadonlySet<Format> = new Set(
      Object.values(Format) as Format[],
    )
    export namespace Format {
      export const ALL: readonly Format[] = Array.from(FORMATS)
      export const isFormat = TypeChecker.Factory.oneOf(FORMATS)
      export const assertIsFormat: TypeAsserter<Format> =
        TypeAsserter.Factory.from(isFormat)
    }

    /**
     * By default, {@link Value.Reference} is expected to be in this format
     */
    export type Default = InFormat.Entity
    export namespace Default {
      // NOTE: this must appear after InFormat has been defined
      export const TYPE = InFormat.Entity.TYPE

      export const isDefault: TypeChecker<Default> = TypeChecker.Factory.lazy(
        () =>
          TypeChecker.Factory.custom(
            (value: unknown): value is Default =>
              TypeChecker.isObject(value) &&
              'entityId' in value &&
              TypeChecker.isString(value.entityId) &&
              'recordId' in value &&
              (TypeChecker.isString(value.recordId) ||
                TypeChecker.isNullish(value.recordId)),
            { typeDescription: 'Value.Reference.Default' },
          ),
      ).usingAlias('Value.Reference.Default')

      export const assertIsDefault: TypeAsserter<Default> =
        TypeAsserter.Factory.from(isDefault)
    }

    export type Multi<
      TFormat extends Format = Format,
      TEntityId extends models.Entity.Id | PartialId =
        | models.Entity.Id
        | PartialId,
    > = {
      [Format.Dimension]: Partial<
        Record<TEntityId, readonly record.Record.Id[]>
      >
      [Format.Entity]: readonly Reference<TFormat, TEntityId>[]
      [Format.EntityId]: readonly TEntityId[]
      [Format.RecordId]: readonly record.Record.Id[]
    }[TFormat]

    export namespace Multi {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export namespace InFormat {
        /**
         * Alias for
         * {@link Value.Reference.Multiple}<{@link Value.Reference.Format.Dimension}>
         */
        export type Dimension = Multi<Format.Dimension>
        export namespace Dimension {
          export const TYPE = common.value.types.Structure.object<Dimension>({
            known: {},
            // Record IDs
            others: common.value.types.Intrinsic.List,
          })
        }

        /**
         * Alias for
         * {@link Value.Reference.Multi}<{@link Value.Reference.Format.Entity}>
         */
        export type Entity = Multi<Format.Entity>
        export namespace Entity {
          export const TYPE = common.value.types.Structure.list(
            Reference.InFormat.Entity.TYPE,
          )
        }

        /**
         * Alias for
         * {@link Value.Reference.Multi}<{@link Value.Reference.Format.EntityId}>
         */
        export type EntityId = Multi<Format.EntityId>
        export namespace EntityId {
          export const TYPE = common.value.types.Intrinsic.List
        }

        /**
         * Alias for
         * {@link Value.Reference.Multi}<{@link Value.Reference.Format.RecordId}>
         */
        export type RecordId = Multi<Format.RecordId>
        export namespace RecordId {
          export const TYPE = common.value.types.Intrinsic.List
        }
      }

      /**
       * By default, {@link Value.Reference.Multi} is expected to be in this format
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export type Default = InFormat.Dimension
      export namespace Default {
        // NOTE: this must appear after InFormat has been defined
        export const TYPE = InFormat.Dimension.TYPE

        export const isDefault: TypeChecker<Default> = TypeChecker.Factory.lazy(
          () =>
            TypeChecker.Factory.custom(
              (value: unknown): value is Default =>
                TypeChecker.isObject(value) &&
                Object.keys(value).length > 0 &&
                Object.values(value).every(v => TypeChecker.isArray(v)),
              { typeDescription: 'Value.Reference.Multi.Default' },
            ),
        ).usingAlias('Value.Reference.Multi.Default')

        export const assertIsDefault: TypeAsserter<Default> =
          TypeAsserter.Factory.from(isDefault)
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Value Serialization
  // ---------------------------------------------------------------------------

  /**
   * Tries to reconstruct a DMS class instance from a plain value.
   *
   * If the value is a plain object with a known instance envelope key
   * (`$date`, `$range`, `$multirange`), the corresponding helper's
   * `fromPlain` method is called.
   *
   * If the value is not an instance envelope, it is returned as-is.
   */
  export function fromPlainValue(value: unknown): unknown {
    if (TypeChecker.isNullish(value) || !TypeChecker.isObject(value)) {
      return value
    }
    const key = expression.instance.Key.from(
      value as expression.instance.Instance,
    )
    if (key) {
      return expression.instance.HELPERS_LOOKUP[key].fromPlain(
        value as expression.instance.Instance,
      )
    }
    return value
  }

  /**
   * Checks if a runtime value is already an instance of any DMS class
   * (e.g., `DmsDate`, `DmsRange`, `DmsMultiRange`).
   */
  export function isDmsInstance(value: unknown): boolean {
    return expression.instance
      .getAllHelpers()
      .some(helper => helper.runtimeTypeChecker(value))
  }

  /**
   * Converts a DMS class instance to its plain JSON envelope form.
   *
   * If the value is a DMS instance (e.g., `DmsDate`), calls its `toJSON()`.
   * Otherwise returns `undefined`.
   */
  export function toPlainValue(value: unknown): unknown | undefined {
    for (const helper of expression.instance.getAllHelpers()) {
      if (helper.runtimeTypeChecker(value)) {
        // DMS class instances have toJSON() methods
        // TODO: our helper, should actually have toJSON
        return (value as { toJSON(): unknown }).toJSON()
      }
    }
    return undefined
  }

  /**
   * Recursively converts a value tree to its JSON-safe plain-object form.
   *
   * Transforms DMS class instances (`DmsDate`, `DmsRange`, `DmsMultiRange`)
   * into their envelope format (`{ $date: ... }`, `{ $range: ... }`, etc.)
   * and recursively walks arrays and plain objects.
   *
   * Replaces the `JSON.parse(JSON.stringify(value))` pattern.
   */
  export function toJson(value: unknown): unknown {
    if (value === null || value === undefined) {
      return value
    }

    const converted = toPlainValue(value)
    if (converted !== undefined) {
      // Recursively process — e.g. DmsRange.toJSON() returns an object with
      // DmsDate endpoints that also need conversion
      return toJson(converted)
    }

    if (Array.isArray(value)) {
      return value.map(item => toJson(item))
    }

    if (TypeChecker.isDate(value)) {
      return value.toJSON()
    }

    if (TypeChecker.isObject(value)) {
      const result: Record<string, unknown> = {}
      for (const [key, val] of Object.entries(value)) {
        result[key] = toJson(val)
      }
      return result
    }

    return value
  }

  /**
   * Recursively reconstructs DMS class instances from their plain JSON
   * envelope form (`{ $date: ... }`, `{ $range: ... }`, `{ $multirange: ... }`).
   *
   * This is the inverse of {@link toJson}.
   */
  export function fromJson(value: unknown): unknown {
    if (TypeChecker.isNullish(value) || TypeChecker.isPrimitive(value)) {
      return value
    }

    if (TypeChecker.isObject(value) && !Array.isArray(value)) {
      const key = expression.instance.Key.from(
        value as expression.instance.Instance,
      )
      if (key) {
        return expression.instance.HELPERS_LOOKUP[key].fromPlain(
          value as expression.instance.Instance,
        )
      }
      const result: Record<string, unknown> = {}
      for (const [k, val] of Object.entries(value)) {
        result[k] = fromJson(val)
      }
      return result
    }

    if (Array.isArray(value)) {
      return value.map(item => fromJson(item))
    }

    return value
  }

  // ---------------------------------------------------------------------------
  // PG Native String Conversion
  // ---------------------------------------------------------------------------

  /**
   * Maps each DMS instance {@link expression.instance.Key} to the
   * {@link common.value.types.Intrinsic} whose PG `to_jsonb()` serializes as
   * that instance's string form, or `null` if the instance has no PG string
   * representation.
   *
   * Keyed by instance key so that adding a new instance type forces a compile
   * error here until the mapping is updated.
   */
  const _INSTANCE_KEY_TO_PG_INTRINSIC: Record<
    expression.instance.Key,
    common.value.types.Intrinsic | null
  > = {
    // NOTE: PG serializes dates as plain ISO strings — no special conversion needed
    $date: null,
    $multirange: common.value.types.Intrinsic.DateMultirange,
    $range: common.value.types.Intrinsic.DateRange,
  }

  /** Derived reverse lookup: {@link common.value.types.Intrinsic} → {@link expression.instance.Key} */
  const _PG_INTRINSIC_TO_KEY = new Map(
    (
      Object.entries(_INSTANCE_KEY_TO_PG_INTRINSIC) as [
        expression.instance.Key,
        common.value.types.Intrinsic | null,
      ][]
    )
      .filter(
        (
          entry,
        ): entry is [expression.instance.Key, common.value.types.Intrinsic] =>
          entry[1] !== null,
      )
      .map(([key, intrinsic]) => [intrinsic, key] as const),
  )

  /**
   * Returns `true` if the given intrinsic type has a PG native string
   * representation that needs conversion via {@link pgStringToEnvelope}.
   */
  export function hasPgStringRepresentation(
    type: common.value.types.Intrinsic,
  ): boolean {
    return _PG_INTRINSIC_TO_KEY.has(type)
  }

  /**
   * Converts a PostgreSQL native type string to its DMS envelope format.
   *
   * PG's `to_jsonb()` serializes types like `datemultirange` and `daterange`
   * as literal strings (e.g. `"{[2022-01-01,2023-01-01)}"`). This converts
   * them to the DMS envelope format
   * (e.g. `{ $multirange: [...] }`, `{ $range: {...} }`).
   *
   * Returns `undefined` if the intrinsic type doesn't have a PG string
   * representation.
   */
  export function pgStringToEnvelope(
    pgString: string,
    type: common.value.types.Intrinsic,
  ): unknown {
    const key = _PG_INTRINSIC_TO_KEY.get(type)
    if (!key) {
      return undefined
    }
    const helper = expression.instance.HELPERS_LOOKUP[key]
    const instance = helper.fromPgString?.(pgString)
    return instance !== undefined ? toJson(instance) : undefined
  }
}
